"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";

declare global {
    interface Window {
        YT?: any;
        onYouTubeIframeAPIReady?: () => void;
    }
}

type Skin = "neo2002" | "cyberdungeon" | "oled" | "crt";

export type ConsoleVideo = {
    videoId: string;
    title: string;
    channelTitle?: string;
    thumbnailUrl?: string;
};

const YT_STATE = {
    UNSTARTED: -1,
    ENDED: 0,
    PLAYING: 1,
    PAUSED: 2,
    BUFFERING: 3,
    CUED: 5,
} as const;

function loadYouTubeIframeAPI(): Promise<void> {
    return new Promise((resolve) => {
        if (window.YT?.Player) return resolve();

        const existing = document.querySelector(
            'script[src="https://www.youtube.com/iframe_api"]'
        );
        if (existing) {
            const t = window.setInterval(() => {
                if (window.YT?.Player) {
                    window.clearInterval(t);
                    resolve();
                }
            }, 50);
            return;
        }

        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);

        window.onYouTubeIframeAPIReady = () => resolve();
    });
}

function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}

function formatTime(sec: number) {
    if (!Number.isFinite(sec) || sec < 0) return "0:00";
    const s = Math.floor(sec);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
}

type Props = {
    open: boolean;
    videos: ConsoleVideo[];
    initialIndex?: number;
    skin?: Skin;
    onSkinChange?: (skin: Skin) => void;
    onClose: () => void;
    onIndexChange?: (index: number) => void;
};

export default function ConsolePlayer({
    open,
    videos,
    initialIndex = 0,
    skin = "cyberdungeon",
    onSkinChange,
    onClose,
    onIndexChange,
}: Props) {
    const [index, setIndex] = useState(() => clamp(initialIndex, 0, Math.max(0, videos.length - 1)));
    const [playlistOpen, setPlaylistOpen] = useState(true);
    const [playing, setPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });

    // Fullscreen state (tracks browser fullscreen)
    const [isFullscreen, setIsFullscreen] = useState(false);

    const skinColors = {
        cyberdungeon: {
            bg: '#0a0805',
            panel: 'rgb(45, 25, 15)',
            panel2: 'rgb(35, 18, 10)',
            accent: '#ff6b35',
            led: '#ff4757'
        },
        neo2002: {
            bg: '#0a0f0a',
            panel: 'rgb(20, 45, 20)',
            panel2: 'rgb(12, 35, 12)',
            accent: '#10b981',
            led: '#34d399'
        },
        oled: {
            bg: '#0f0f1a',
            panel: 'rgb(35, 35, 65)',
            panel2: 'rgb(25, 25, 50)',
            accent: '#0088ff',
            led: '#00ccff'
        },
        crt: {
            bg: '#0f050a',
            panel: 'rgb(35, 15, 45)',
            panel2: 'rgb(25, 10, 35)',
            accent: '#ff0080',
            led: '#ff66b2'
        }
    };

    useEffect(() => {
        const modalElement = document.querySelector('[data-skin]') as HTMLElement;
        if (modalElement && skinColors[skin]) {
            const colors = skinColors[skin];
            modalElement.style.setProperty('--console-bg', colors.bg);
            modalElement.style.setProperty('--console-panel', colors.panel);
            modalElement.style.setProperty('--console-panel2', colors.panel2);
            modalElement.style.setProperty('--console-accent', colors.accent);
            modalElement.style.setProperty('--console-led', colors.led);
        }
    }, [skin]);

    // Track fullscreen state with event listener
    useEffect(() => {
        const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
        document.addEventListener("fullscreenchange", onFsChange);
        onFsChange(); // Check initial state
        return () => document.removeEventListener("fullscreenchange", onFsChange);
    }, []);

    const playerRef = useRef<any>(null);
    const containerIdRef = useRef(`yt-console-${Math.random().toString(36).slice(2)}`);
    const modalRef = useRef<HTMLDivElement>(null);
    const pollRef = useRef<number | null>(null);

    const handleDragStart = useCallback((e: React.MouseEvent) => {
        setIsDragging(true);
        setDragStart({ x: e.clientX - modalPosition.x, y: e.clientY - modalPosition.y });
        e.preventDefault();
    }, [modalPosition]);

    const handleDragMove = useCallback((e: MouseEvent) => {
        if (!isDragging) return;
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;
        const constrainedX = Math.max(-100, Math.min(window.innerWidth - 300, newX));
        const constrainedY = Math.max(-100, Math.min(window.innerHeight - 300, newY));
        setModalPosition({ x: constrainedX, y: constrainedY });
    }, [isDragging, dragStart]);

    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleDragMove);
            document.addEventListener('mouseup', handleDragEnd);
            document.body.style.cursor = 'grabbing';
            document.body.style.userSelect = 'none';
        } else {
            document.removeEventListener('mousemove', handleDragMove);
            document.removeEventListener('mouseup', handleDragEnd);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }
        return () => {
            document.removeEventListener('mousemove', handleDragMove);
            document.removeEventListener('mouseup', handleDragEnd);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isDragging, handleDragMove, handleDragEnd]);

    // Fullscreen functionality
    const toggleFullscreen = useCallback(() => {
        if (!document.fullscreenElement) {
            // Enter fullscreen - use browser fullscreen API
            const modalElement = modalRef.current;
            if (modalElement && modalElement.requestFullscreen) {
                modalElement.requestFullscreen();
            }
        } else {
            // Exit fullscreen
            document.exitFullscreen();
        }
    }, []);

    const current = videos[index];
    const hasPrev = index > 0;
    const hasNext = index < videos.length - 1;

    const setIndexSafe = useCallback((next: number) => {
        const clamped = clamp(next, 0, Math.max(0, videos.length - 1));
        setIndex(clamped);
        onIndexChange?.(clamped);
    }, [videos.length, onIndexChange]);

    const goPrev = useCallback(() => {
        if (!hasPrev) return;
        setIndexSafe(index - 1);
    }, [hasPrev, index, setIndexSafe]);

    const goNext = useCallback(() => {
        if (!hasNext) return;
        setIndexSafe(index + 1);
    }, [hasNext, index, setIndexSafe]);

    // Mouse wheel functionality
    const wheelCooldownRef = useRef(false);

    const onWheel = useCallback(
        (e: React.WheelEvent) => {
            if (wheelCooldownRef.current) return;
            e.preventDefault();

            if (e.deltaY > 0) goNext();
            else if (e.deltaY < 0) goPrev();

            wheelCooldownRef.current = true;
            window.setTimeout(() => (wheelCooldownRef.current = false), 600);
        },
        [goNext, goPrev]
    );

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;

        (async () => {
            await loadYouTubeIframeAPI();
            if (cancelled) return;

            playerRef.current = new window.YT.Player(containerIdRef.current, {
                videoId: current?.videoId,
                playerVars: {
                    autoplay: 1,
                    controls: 1,
                    fs: 1,
                    playsinline: 1,
                    rel: 0,
                    modestbranding: 1,
                },
                events: {
                    onReady: () => {
                        console.log('YouTube player ready');
                    },
                    onStateChange: (e: any) => {
                        const st = e.data as number;
                        if (st === YT_STATE.PLAYING) setPlaying(true);
                        if (st === YT_STATE.PAUSED) setPlaying(false);
                        if (st === YT_STATE.ENDED) {
                            setPlaying(false);
                            if (hasNext) goNext();
                        }
                    },
                },
            });
        })();

        return () => {
            cancelled = true;
            setPlaying(false);
            setCurrentTime(0);
            setDuration(0);
            if (pollRef.current) window.clearInterval(pollRef.current);
            pollRef.current = null;

            try {
                playerRef.current?.destroy?.();
            } catch { }
            playerRef.current = null;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (!playerRef.current?.loadVideoById) return;
        if (!current?.videoId) return;

        setCurrentTime(0);
        setDuration(0);
        playerRef.current.loadVideoById(current.videoId);
    }, [open, current?.videoId]);

    useEffect(() => {
        if (!open) return;

        if (pollRef.current) window.clearInterval(pollRef.current);
        pollRef.current = window.setInterval(() => {
            try {
                const p = playerRef.current;
                if (!p) return;
                const t = Number(p.getCurrentTime?.() ?? 0);
                const d = Number(p.getDuration?.() ?? 0);
                if (Number.isFinite(t)) setCurrentTime(t);
                if (Number.isFinite(d) && d > 0) setDuration(d);
            } catch { }
        }, 250);

        return () => {
            if (pollRef.current) window.clearInterval(pollRef.current);
            pollRef.current = null;
        };
    }, [open]);

    const togglePlay = useCallback(() => {
        const p = playerRef.current;
        if (!p) return;
        try {
            const st = p.getPlayerState?.();
            if (st === YT_STATE.PLAYING) p.pauseVideo?.();
            else p.playVideo?.();
        } catch { }
    }, []);

    const seekTo = useCallback((ratio: number) => {
        const p = playerRef.current;
        if (!p || !duration) return;
        const t = clamp(ratio, 0, 1) * duration;
        try {
            p.seekTo?.(t, true);
            p.playVideo?.();
        } catch { }
    }, [duration]);

    useEffect(() => {
        if (!open) return;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
            if (e.key === " " || e.key.toLowerCase() === "k") {
                e.preventDefault();
                togglePlay();
            }
            if (e.key.toLowerCase() === "j" || e.key === "ArrowDown" || e.key === "ArrowRight") goNext();
            if (e.key.toLowerCase() === "h" || e.key === "ArrowUp" || e.key === "ArrowLeft") goPrev();
            if (e.key.toLowerCase() === "p") setPlaylistOpen((v) => !v);
            if (e.key === "f" || e.key === "F") {
                e.preventDefault();
                toggleFullscreen();
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, goNext, goPrev, onClose, togglePlay, toggleFullscreen]);

    const progress = duration > 0 ? clamp(currentTime / duration, 0, 1) : 0;

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-2 bg-black/70"
            onWheel={onWheel}
        >
            <div
                ref={modalRef}
                data-skin={skin}
                className={`rounded-2xl overflow-hidden shadow-2xl relative transition-all duration-300 border-2 ${isDragging ? 'cursor-grabbing' : ''}`}
                style={{
                    background: "var(--console-bg)",
                    color: "var(--console-text)",
                    boxShadow: "var(--console-glow)",
                    borderColor: "var(--console-border)",
                    width: "1185px",
                    height: "700px",
                    minWidth: "720px",
                    minHeight: "540px",
                    maxWidth: "85vw",
                    maxHeight: "75vh",
                    transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
                    cursor: 'grab',
                }}
            >
                {/* Top header */}
                <div
                    className="flex items-center justify-between gap-3 px-4 py-3 border-b cursor-grab active:cursor-grabbing"
                    style={{ borderColor: "var(--console-border)" }}
                    onMouseDown={handleDragStart}
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <div
                            className="h-9 w-9 rounded-xl flex items-center justify-center border"
                            style={{ borderColor: "var(--console-border)", background: "var(--console-panel)" }}
                        >
                            <span className="text-sm font-bold" style={{ color: "var(--console-led)" }}>MR</span>
                        </div>
                        <div className="min-w-0">
                            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--console-muted)" }}>
                                Console Player
                            </div>
                            <div className="font-semibold truncate">{current?.title ?? "Loading..."}</div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            className="px-3 py-1.5 rounded-lg text-sm border"
                            style={{ borderColor: "var(--console-border)", background: "var(--console-panel)" }}
                            onClick={() => setPlaylistOpen((v) => !v)}
                            title="Toggle playlist (P)"
                        >
                            Playlist
                        </button>

                        <button
                            className="px-3 py-1.5 rounded-lg text-sm border mr-2"
                            style={{
                                borderColor: "var(--console-border)",
                                background: "var(--console-panel)",
                                color: "var(--console-text)",
                            }}
                            onClick={toggleFullscreen}
                            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                        >
                            {isFullscreen ? "⛶ Exit FS" : "⛶ Fullscreen"}
                        </button>

                        <button
                            className="px-3 py-1.5 rounded-lg text-sm font-semibold"
                            style={{ background: "#fff", color: "#000" }}
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>

                {/* Main content */}
                <div className="flex gap-4 h-full p-4">
                    {/* Video player */}
                    <div className="flex-1 flex flex-col">
                        <div
                            className="flex-1 rounded-2xl overflow-hidden border mb-4"
                            style={{ borderColor: "var(--console-border)", background: "var(--console-panel2)" }}
                        >
                            <div className="w-full bg-black yt-player-container relative h-full">
                                <div id={containerIdRef.current} className="absolute inset-0 w-full h-full pointer-events-auto" />
                            </div>
                        </div>

                        {/* Transport controls */}
                        <div
                            className="p-3 rounded-xl border"
                            style={{
                                borderColor: "var(--console-border)",
                                background: "var(--console-panel)",
                            }}
                        >
                            <div className="flex items-center justify-between gap-3 mb-3">
                                <div className="flex items-center gap-2">
                                    <button
                                        className="px-3 py-2 rounded-xl border disabled:opacity-40"
                                        style={{ borderColor: "var(--console-border)", background: "var(--console-panel)" }}
                                        onClick={goPrev}
                                        disabled={!hasPrev}
                                    >
                                        ⏮
                                    </button>

                                    <button
                                        className="px-4 py-2 rounded-xl border font-semibold"
                                        style={{
                                            borderColor: "var(--console-border)",
                                            background: "var(--console-accent)",
                                            color: "#0b0b0b",
                                        }}
                                        onClick={togglePlay}
                                    >
                                        {playing ? "⏸ Pause" : "▶ Play"}
                                    </button>

                                    <button
                                        className="px-3 py-2 rounded-xl border disabled:opacity-40"
                                        style={{ borderColor: "var(--console-border)", background: "var(--console-panel)" }}
                                        onClick={goNext}
                                        disabled={!hasNext}
                                    >
                                        ⏭
                                    </button>
                                </div>

                                <div className="flex items-center gap-3 shrink-0">
                                    <SpectrumBars active={playing} />
                                    <div className="text-xs font-mono" style={{ color: "var(--console-muted)" }}>
                                        {formatTime(currentTime)} / {formatTime(duration)}
                                    </div>
                                </div>
                            </div>

                            {/* Progress bar */}
                            <div
                                className="h-2 rounded-full overflow-hidden border cursor-pointer"
                                style={{ borderColor: "var(--console-border)", background: "var(--console-panel)" }}
                                onClick={(e) => {
                                    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                                    const ratio = (e.clientX - rect.left) / rect.width;
                                    seekTo(ratio);
                                }}
                            >
                                <div
                                    className="h-full"
                                    style={{
                                        width: `${Math.round(progress * 100)}%`,
                                        background: "var(--console-accent)",
                                        boxShadow: "var(--console-glow)",
                                    }}
                                />
                            </div>

                            <div className="mt-2 text-xs" style={{ color: "var(--console-muted)" }}>
                                Space: play/pause • Arrows: navigate • P: playlist • F: fullscreen • Escape: close
                            </div>
                        </div>
                    </div>

                    {/* Playlist */}
                    {playlistOpen && (
                        <div className="w-80 flex-shrink-0 border-l pt-3 pr-4 pb-4 pl-4" style={{ borderColor: "var(--console-border)", background: "var(--console-panel)" }}>
                            <div className="mb-3">
                                <div className="font-semibold">
                                    Playlist {index + 1}/{videos.length}
                                </div>
                            </div>

                            <div className="space-y-2 overflow-y-auto max-h-[60vh]">
                                {videos.slice(0, 5).map((v, i) => {
                                    const active = i === index;
                                    return (
                                        <button
                                            key={`${v.videoId}-${i}`}
                                            className="w-full text-left rounded-xl border p-2 flex gap-3"
                                            style={{
                                                borderColor: "var(--console-border)",
                                                background: active ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0.15)",
                                                boxShadow: active ? "var(--console-glow)" : "none",
                                            }}
                                            onClick={() => setIndexSafe(i)}
                                        >
                                            <div className="w-20 shrink-0">
                                                {v.thumbnailUrl ? (
                                                    <img
                                                        src={v.thumbnailUrl}
                                                        alt=""
                                                        className="w-20 h-12 object-cover rounded-lg"
                                                    />
                                                ) : (
                                                    <div className="w-20 h-12 rounded-lg bg-black/40 border" style={{ borderColor: "var(--console-border)" }} />
                                                )}
                                            </div>

                                            <div className="min-w-0">
                                                <div className="text-sm font-semibold line-clamp-2">{v.title}</div>
                                                {v.channelTitle && (
                                                    <div className="text-xs mt-1 truncate" style={{ color: "var(--console-muted)" }}>
                                                        {v.channelTitle}
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Skin selector */}
                            <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--console-border)" }}>
                                <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--console-muted)" }}>
                                    Skin
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    {(["neo2002", "cyberdungeon", "oled", "crt"] as Skin[]).map((s) => (
                                        <SkinButton
                                            key={s}
                                            skin={s}
                                            active={skin === s}
                                            onClick={() => onSkinChange?.(s)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function SpectrumBars({ active }: { active: boolean }) {
    const [heights, setHeights] = useState<number[]>(Array(18).fill(6));

    useEffect(() => {
        if (!active) {
            setHeights(Array(18).fill(6));
            return;
        }

        const interval = setInterval(() => {
            setHeights(prev => prev.map((_, i) => {
                const time = Date.now() * 0.001 + (i % 6) * 0.08;
                const bounce = Math.sin(time * (2 + i % 3)) * 0.5 + 0.5;
                return 6 + bounce * 26; // 6px to 32px range
            }));
        }, 50);

        return () => clearInterval(interval);
    }, [active]);

    return (
        <div className="flex items-end gap-[3px] h-10 shrink-0 overflow-visible" style={{ minWidth: '120px' }}>
            {Array.from({ length: 18 }).map((_, i) => (
                <div
                    key={i}
                    className="w-[6px] rounded-sm block"
                    style={{
                        height: `${heights[i]}px`,
                        backgroundColor: active ? "#7c3aed" : "#666",
                        boxShadow: active ? "0 0 8px rgba(124,58,237,0.6)" : "none",
                        transition: "height 0.05s ease-out",
                    }}
                />
            ))}
        </div>
    );
}

function SkinButton({ skin, active, onClick }: { skin: Skin; active: boolean; onClick: () => void }) {
    const label =
        skin === "neo2002" ? "Neo 2002" :
            skin === "cyberdungeon" ? "Cyber" :
                skin === "oled" ? "OLED" : "CRT";

    return (
        <div
            className="rounded-xl border p-2 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={onClick}
            style={{
                borderColor: "var(--console-border)",
                background: active ? "var(--console-panel)" : "var(--console-panel2)",
            }}
        >
            <div className="text-sm font-semibold">{label}</div>
            <div className="text-xs" style={{ color: "var(--console-muted)" }}>
                {skin}
            </div>
        </div>
    );
}